/**
 * Browser policy logs that are NOT app breakage.
 * Firefox/Chrome surface CSP-blocked eval from framework chunks as console
 * "error" even when the page still hydrates and works — that means CSP is
 * doing its job (Missing 'unsafe-eval'), not that the UI is broken.
 */

/** Crawlers/previews that execute JS against stale deploys and spam ChunkLoadError. */
const BOT_UA_RE =
  /bot|crawler|spider|slurp|storebot|google-inspection|bytespider|semrush|ahrefs|pingdom|uptimerobot|headlesschrome|lighthouse|chrome-lighthouse|yandex|baidu|duckduck|bingpreview|linkedinbot|twitterbot|applebot|petalbot|gptbot|claudebot|anthropic|ccbot|facebookexternalhit|preview|curl\/|wget\/|python-requests|scrapy|httpclient|go-http-client/i;

export function isBotUserAgent(userAgent: string | undefined | null): boolean {
  if (!userAgent) return false;
  return BOT_UA_RE.test(userAgent);
}

/** Next/Turbopack chunk miss after deploy or flaky network. */
export function isChunkLoadFailure(message: string): boolean {
  const text = message.toLowerCase();
  if (!text) return false;
  return (
    text.includes("failed to load chunk") ||
    text.includes("chunkloaderror") ||
    text.includes("loading chunk") ||
    text.includes("loading css chunk") ||
    (text.includes("dynamically imported module") &&
      (text.includes("failed") || text.includes("error")))
  );
}

/** Benign View Transition API lifecycle rejections (spec-expected, not app bugs). */
export function isBenignViewTransitionNoise(message: string): boolean {
  const text = message.toLowerCase().trim();
  if (!text) return false;
  if (!text.includes("transition")) return false;
  // Chromium 138-146+: "Transition was aborted because of invalid state" (generic) or
  // "View transition was skipped because document visibility state is hidden"
  // + TanStack/SPA routers: AbortError/TimeoutError mid-transition are expected races.
  if (
    text.includes("transition was aborted") ||
    text.includes("transition was skipped") ||
    text.includes("view transition was skipped") ||
    text.includes("visibility state is hidden") ||
    text.includes("old view transition aborted") ||
    text.includes("transition was aborted because of timeout") ||
    (text.includes("invalidstateerror") && text.includes("transition")) ||
    (text.includes("aborterror") && text.includes("transition")) ||
    (text.includes("timeouterror") && text.includes("transition"))
  ) {
    return true;
  }
  // DOMException name + transition wording already covered; narrow fallback for raw name
  return false;
}

export function isBenignBrowserConsoleNoise(message: string): boolean {
  const text = message.toLowerCase().trim();
  if (!text) return false;

  // 2026-08-29 preload warning — Chrome: "The resource <URL> was preloaded using link preload but not used within a few seconds"
  // Harmless when speculationrules prefetch CSS/fonts for routes not visited in <3s; suppress to keep Discord clean.
  if (text.includes("was preloaded using link preload but not used"))
    return true;
  if (text.includes("was preloaded using link preload")) return true;

  // 2026-08-29 web-vitals Speed Insights: "Cannot read properties of undefined (reading 'startTime')" when metric.entries[0] is undefined
  // Caused by extension-injected PerformanceObserver polyfill (inject.js) or adblock; harmless, suppress.
  if (
    text.includes("starttime") &&
    text.includes("cannot read properties of undefined")
  )
    return true;
  if (text.includes("starttime") && text.includes("cannot read property"))
    return true;
  if (text.includes("reportallchanges") && text.includes("starttime"))
    return true;

  // 2026-09-10 Sentry JAVASCRIPT-NEXTJS-18: "Cannot read properties of
  // undefined (reading 'prototype')" con frames ext:core/01_core.js +
  // next/constants.ts — chunk de terceros / content-script, nunca código app
  // (el repo no lee `.prototype` en runtime salvo tests). Se suprime el
  // mensaje pelado: si algún día el código app leyera .prototype, typecheck +
  // tests lo atraparían antes que este filtro.
  if (
    text.includes("reading 'prototype'") ||
    text.includes('reading "prototype"')
  ) {
    return true;
  }

  // 2026: View Transition benign aborts (dashboard:1 InvalidStateError spam when tab hidden)
  if (isBenignViewTransitionNoise(text)) return true;

  // Extension PDF editors (e.g. "pdfeditor is ready! inject.js:1") inject noise on every page
  if (
    text.includes("pdfeditor is ready") ||
    (text.includes("inject.js") && text.includes("pdfeditor")) ||
    text.includes("pdf editor is ready")
  ) {
    return true;
  }

  if (
    text.includes("content-security-policy") &&
    text.includes("unsafe-eval")
  ) {
    return true;
  }
  if (text.includes("content security policy") && text.includes("eval")) {
    return true;
  }
  // Firefox wraps the same CSP line in a JavaScript Error envelope
  if (
    text.includes("missing 'unsafe-eval'") ||
    (text.includes("blocked a javascript eval") && text.includes("script-src"))
  ) {
    return true;
  }
  // Chrome / Chromium phrasing (CSP+eval already covered above)
  if (
    text.includes("refused to evaluate a string as javascript") ||
    text.includes("evaluating a string as javascript violates")
  ) {
    return true;
  }
  // securitypolicyviolation / report wording that sometimes hits console.error
  if (text.includes("securitypolicyviolation") && text.includes("eval")) {
    return true;
  }

  // Soft client polls: usage refresh on app workspaces can fail on
  // offline, tab sleep, or navigation abort — not an app bug.
  if (
    text.includes("failed to refresh usage") ||
    text.includes("usage refresh skipped") ||
    text.includes("refreshusage failed") ||
    text.includes("failed to send error report")
  ) {
    return true;
  }
  // Browser TypeError for aborted/offline fetch of background polls
  if (
    (text.includes("failed to fetch") ||
      text.includes("networkerror") ||
      text.includes("load failed")) &&
    (text.includes("usage") || text.includes("abort"))
  ) {
    return true;
  }
  if (
    text.includes("aborterror") ||
    text.includes("the user aborted a request")
  ) {
    return true;
  }

  // 2026: Transient network resets on background API polls (ERR_CONNECTION_RESET / ERR_FAILED / ERR_NETWORK_CHANGED)
  // Produced by Chrome when tab sleeps or extension throttles fetch — not an app bug.
  if (
    text.includes("err_connection_reset") ||
    text.includes("err_connection_closed") ||
    text.includes("err_connection_refused") ||
    text.includes("err_network_changed") ||
    text.includes("err_failed") ||
    text.includes("net::err_")
  ) {
    if (
      text.includes("usage") ||
      text.includes("/api/") ||
      text.includes("vortixy")
    )
      return true;
    // Also suppress generic VM## injected fetch wrappers (extensions re-wrap fetch and re-throw)
    if (text.includes("vm") && text.includes("/api/usage")) return true;
  }

  // 2026 a11y: Chrome warns "Blocked aria-hidden on an element because its descendant retained focus"
  // when sidebar closes while a thread link has focus. Fixed in chat-sidebar.tsx (inert + focus move),
  // but suppress residual console noise so it doesn't page Discord.
  if (
    text.includes("blocked aria-hidden") &&
    text.includes("descendant retained focus")
  ) {
    return true;
  }

  // ChunkLoadError during intentional post-deploy recovery reload (session flag).
  if (text.includes("chunkreload") || text.includes("[chunk-reload]")) {
    return true;
  }

  // Cross-origin "Script error." — browser strips details; almost never our code.
  // (window.onerror with no CORS headers → empty message "Script error.")
  // Match strictly as the whole message (with optional punctuation/wrapping).
  if (isOpaqueScriptErrorMessage(text)) {
    return true;
  }

  // Browser extensions / injected wallets (Brave, MetaMask, reader modes, etc.).
  // These throw on every page; stack is usually "global code@https://site/:1:N".
  if (isBrowserExtensionInjectionNoise(text)) {
    return true;
  }

  // 2026-09-03 extension truncated-payload SyntaxError (pdfeditor inject.js on
  // /chat → "chat:1 Uncaught SyntaxError: Unexpected end of input"). The page
  // URL becomes the error file, so attribution needs the stack — callers pass
  // combined message+stack+filename text.
  if (isExtensionTruncatedPayloadNoise(text)) {
    return true;
  }

  // 2026-10-05 Sentry JAVASCRIPT-NEXTJS-20/1Z: timer eval'ado de terceros
  // (`start is not defined` + frame eval/setInterval) — nunca código app.
  if (isThirdPartyTimerNoise(text)) {
    return true;
  }

  return false;
}

/**
 * Opaque cross-origin script errors (no stack, no details). Not actionable.
 * Covers "Script error.", "Script error", "Uncaught Script error.", etc.
 */
export function isOpaqueScriptErrorMessage(message: string): boolean {
  const text = message.toLowerCase().trim();
  if (!text) return false;
  // Exact / near-exact forms only — avoid swallowing real "script error in foo.ts:12"
  if (text === "script error." || text === "script error") return true;
  if (text === "uncaught script error." || text === "uncaught script error")
    return true;
  if (
    text === "javascript error: script error." ||
    text === "javascript error: script error"
  ) {
    return true;
  }
  // Error envelopes that only carry the opaque phrase
  if (/^(uncaught\s+)?(error:\s*)?script error\.?$/i.test(text)) return true;
  if (/^javascript error:\s*script error\.?$/i.test(text)) return true;
  return false;
}

/**
 * Errors from extensions that inject into the page (wallets, reader modes,
 * password managers). Not Vortixy code — do not page Discord.
 */
export function isBrowserExtensionInjectionNoise(message: string): boolean {
  const text = message.toLowerCase().trim();
  if (!text) return false;

  // MetaMask / Brave wallet / other Web3 injectors
  if (
    text.includes("window.ethereum") ||
    text.includes("ethereum.selectedaddress") ||
    text.includes("ethereum.request") ||
    text.includes("ethereum.providers") ||
    (text.includes("selectedaddress") && text.includes("ethereum"))
  ) {
    return true;
  }

  // Firefox / Brave reader-mode shims that expect a `firefox` global
  // e.g. "Can't find variable: firefox"
  // e.g. "undefined is not an object (evaluating 'window.firefox.reader')"
  if (
    text.includes("can't find variable: firefox") ||
    text.includes("cannot find variable: firefox") ||
    text.includes("firefox is not defined") ||
    text.includes("window.firefox") ||
    (text.includes("firefox.reader") &&
      (text.includes("undefined") ||
        text.includes("null") ||
        text.includes("not an object")))
  ) {
    return true;
  }

  // Generic extension / content-script frames
  if (
    text.includes("chrome-extension://") ||
    text.includes("moz-extension://") ||
    text.includes("safari-extension://") ||
    text.includes("safari-web-extension://") ||
    text.includes("webkit-masked-url://") ||
    text.includes("ms-browser-extension://")
  ) {
    return true;
  }

  // PDF editor / screen capture extensions that inject inject.js (e.g. "pdfeditor is ready! inject.js:1")
  if (text.includes("pdfeditor") || text.includes("pdf editor")) {
    return true;
  }
  // Bare inject.js from generic content-scripts — treat as noise only with explicit extension marker
  if (text.includes("inject.js") && text.includes("is ready")) {
    return true;
  }

  // Urban VPN Proxy / content-script executor (`200.js:1` / `executors/200.js:1`)
  // throwing `TypeError: Cannot read properties of undefined (reading 'M_ID')`
  // when Chrome formats the frame without the `chrome-extension://` prefix.
  if (
    text.includes("reading 'm_id'") &&
    (text.includes("200.js") || text.includes("executors/200.js"))
  ) {
    return true;
  }

  // Common wallet / inject globals besides ethereum
  if (
    text.includes("window.solana") ||
    text.includes("window.web3") ||
    text.includes("__firefox__") ||
    text.includes("__mozwebext__")
  ) {
    return true;
  }

  return false;
}

/**
 * Truncated-payload SyntaxError thrown by extension content scripts running in
 * page context (2026-09-03: pdfeditor inject.js on /chat surfaces as
 * "chat:1 Uncaught SyntaxError: Unexpected end of input (at chat:1:13267)"
 * next to "inject.js:1 pdfeditor is ready!"). The extension parses a truncated
 * payload (JSON.parse/eval of a cut buffer) and the page URL becomes the error
 * file, so attribution REQUIRES an extension marker in the combined
 * message+stack+filename text. Narrow by design: a bare truncation error with
 * no extension marker still reports (fail-open — real app bugs stay visible).
 * All app JSON.parse paths are try/catch-guarded, so an uncaught truncation
 * SyntaxError with an extension frame is never ours.
 */
export function isExtensionTruncatedPayloadNoise(message: string): boolean {
  const text = message.toLowerCase().trim();
  if (!text) return false;
  const isTruncation =
    text.includes("unexpected end of input") ||
    text.includes("unexpected end of json input") ||
    text.includes("unterminated string");
  if (!isTruncation) return false;
  return (
    text.includes("inject.js") ||
    text.includes("pdfeditor") ||
    text.includes("pdf editor") ||
    text.includes("chrome-extension://") ||
    text.includes("moz-extension://") ||
    text.includes("safari-extension://") ||
    text.includes("safari-web-extension://") ||
    text.includes("webkit-masked-url://") ||
    text.includes("ms-browser-extension://")
  );
}

/**
 * Timer de terceros con eval (content-script / extensión / bot con JS
 * inyectado).
 * 2026-10-05 Sentry JAVASCRIPT-NEXTJS-20/1Z: `start is not defined` desde un
 * callback de setInterval eval'ado — stack con frame `(eval)` + wrap de
 * Sentry (mechanism auto.browser.browserapierrors.setInterval), IPs de
 * datacenter, ráfaga cada ~100ms. El bundle propio nunca usa timers con
 * string ni eval (0 `setInterval("…")` en src; CSP sin unsafe-eval lo
 * bloquearía igual), así que el marcador eval/setInterval es obligatorio:
 * un `start is not defined` pelado desde código propio sigue reportando
 * (fail-open).
 */
export function isThirdPartyTimerNoise(message: string): boolean {
  const text = message.toLowerCase().trim();
  if (!text) return false;
  if (!text.includes("start is not defined")) return false;
  return (
    text.includes("(eval)") ||
    text.includes("eval at") ||
    text.includes("setinterval") ||
    text.includes("browserapierrors") ||
    text.includes("chrome-extension://") ||
    text.includes("moz-extension://") ||
    text.includes("safari-extension://") ||
    text.includes("safari-web-extension://") ||
    text.includes("webkit-masked-url://") ||
    text.includes("ms-browser-extension://") ||
    text.includes("inject.js")
  );
}

/**
 * React hydration mismatch (error #418 / "A tree hydrated but...").
 * 2026-09-08 Sentry JAVASCRIPT-NEXTJS-K + JAVASCRIPT-NEXTJS-6: en producción
 * llegan como `Error: {}` (rejection con objeto plano) o como el texto de
 * hydration con stack minificado — ruido transitorio de SSR/hidratación
 * (extensiones, HTML stale post-deploy), no bug accionable. Se suprime en
 * cliente y en /api/error-report; el ErrorBoundary ya reintenta 1×.
 */
export function isHydrationMismatchNoise(message: string): boolean {
  const text = message.toLowerCase();
  if (!text) return false;
  if (text === "{}" || text === "unknown client error") return true;
  return (
    text.includes("a tree hydrated but") ||
    text.includes("hydration") ||
    text.includes("minified react error #418") ||
    text.includes("react error #418")
  );
}

/**
 * Fallo de compilación CSS en dev (PostCSS/Tailwind/Turbopack).
 * 2026-09-24 Sentry JAVASCRIPT-NEXTJS-1V: error de parseo CSS en dev con
 * token inesperado Delim('*') — un typo transitorio de clase fluida con
 * asterisco durante la edición de fluid-type (el literal ya no existe en el
 * árbol; la redacción evita el token para que el scanner de Tailwind no
 * genere una regla rota) capturado por la auto-instrumentación del SDK
 * (mechanism auto.browser, transaction /_error, environment development,
 * 0 usuarios, localhost). Nunca es bug accionable:
 * el CSS de prod se compila en build y un typo así tumba el dev-server, no a
 * usuarios. Research 2026-09-24 docs.sentry.io/platforms/javascript/guides/nextjs
 * (beforeSend + ignoreErrors como red de seguridad del SDK; source maps vía
 * SENTRY_AUTH_TOKEN; tracesSampleRate 1.0 dev / 0.15 prod; sendDefaultPii
 * false + maskAllText en replay).
 * Fail-open por diseño: exige marcador CSS explícito — un SyntaxError JS
 * pelado o un TypeError de app nunca se suprime aquí.
 */
export function isCssCompileNoise(message: string): boolean {
  const text = message.toLowerCase().trim();
  if (!text) return false;
  const hasCssMarker =
    text.includes("parsing css source code failed") ||
    text.includes("postcss transform") ||
    text.includes("postcss") ||
    (text.includes("globals.css") &&
      (text.includes("unexpected token") ||
        text.includes("parse") ||
        text.includes("delim(")));
  if (!hasCssMarker) return false;
  return true;
}

/**
 * Server + client gate for Discord noise.
 * Suppresses known-benign console noise, all crawler traffic, and
 * first-chance ChunkLoadError (auto-reload path) that is not actionable.
 */
export function shouldSuppressClientErrorReport(
  message: string,
  userAgent?: string | null,
): boolean {
  if (isBenignBrowserConsoleNoise(message)) return true;
  if (isHydrationMismatchNoise(message)) return true;
  // 2026-09-24 Sentry JAVASCRIPT-NEXTJS-1V: fallo de compilación CSS en dev
  // (PostCSS/Turbopack) — ruido local, nunca issue.
  if (isCssCompileNoise(message)) return true;
  // Crawlers (Storebot-Google, Googlebot, etc.) hit stale HTML/chunks constantly.
  if (isBotUserAgent(userAgent)) return true;
  return false;
}

/** sessionStorage key: timestamp of last chunk-miss auto-reload (ms). */
export const CHUNK_RELOAD_SESSION_KEY = "vortixy_chunk_reload_at";
